/**
 * `POST /v2/auth/web/refresh`: a new access token for the session.
 *
 * The refresh token is in hg-auth's cookie; the browser sends it by itself
 * to this one address. The request names the session it means and carries
 * `X-Requested-With`, which a form on another page cannot add.
 *
 * hg-auth's rules, followed here:
 * - a `401` (or a `403`: blocked) means "sign in again", so the session ends;
 * - a `5xx` or no answer means "keep the session and retry later".
 *
 * Plain `fetch` on purpose: the API client calls this when a request comes
 * back `401`, so it cannot itself go through the client.
 */
import i18n from "i18next";

import type { components } from "@/shared/api/auth.schema";
import { networkProblem, problemFromResponse, ProblemError } from "@/shared/api/problem";
import { serviceUrl } from "@/shared/api/services";
import { DEFAULT_LANGUAGE } from "@/shared/i18n/languages";

import { endSession, getSessionId, startSession } from "./session";

type WebSessionRequest = components["schemas"]["WebSessionRequest"];
type RefreshResponse = components["schemas"]["Data_TokenResponse"];

/** What hg-auth's web endpoints ask for in `X-Requested-With`: any value; this one says who asks. */
export const WEB_REQUEST = "hg-control-center";

let inFlight: Promise<void> | null = null;

/** Callers that arrive while a refresh is under way share it. */
export function refreshSession(): Promise<void> {
  inFlight ??= exchange().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function exchange(): Promise<void> {
  const sessionId = getSessionId();
  if (sessionId === null) {
    throw new ProblemError({ status: 401, code: "unauthenticated", title: "Not signed in" });
  }

  const request: WebSessionRequest = { session_id: sessionId };
  let response: Response;
  try {
    response = await fetch(`${serviceUrl("auth")}/v2/auth/web/refresh`, {
      method: "POST",
      // The cookie travels to the app's own origin, where hg-auth is served.
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": i18n.resolvedLanguage ?? DEFAULT_LANGUAGE,
        "X-Requested-With": WEB_REQUEST,
      },
      body: JSON.stringify(request),
    });
  } catch (cause) {
    throw networkProblem(cause);
  }

  if (!response.ok) {
    const problem = await problemFromResponse(response);
    // Signed out, or another session meanwhile: this answer is about one that is gone.
    if ((response.status === 401 || response.status === 403) && getSessionId() === sessionId) {
      endSession(problem.text("reason") ?? problem.code);
    }
    throw problem;
  }

  const { data } = (await response.json()) as RefreshResponse;
  // The session moved on while this was in the air (signed out, or signed in anew).
  if (getSessionId() !== sessionId) return;
  startSession(data);
}
